import { Link } from "react-router-dom";

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="2026-05-13">
      <p>
        DocLens is operated by Trijazel. We take privacy seriously and structure the product so
        that we collect the minimum data needed to deliver the service.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Email address</strong> — used to authenticate you and recover your account.
        </li>
        <li>
          <strong>Documents you upload</strong> — sent to Anthropic for inference. We do not retain
          the file bytes after processing; we keep a usage record (file size, MIME type, schema
          used, success/failure) for billing and abuse prevention.
        </li>
        <li>
          <strong>Service logs</strong> — IP addresses, user-agents, error stack traces. Used for
          debugging and abuse prevention. Retained for 30 days.
        </li>
      </ul>

      <h2>What we don't do</h2>
      <ul>
        <li>We do not train AI models on your documents.</li>
        <li>We do not sell or share your data with third parties for marketing.</li>
        <li>We do not use your documents to improve our service or our prompts without your written consent.</li>
      </ul>

      <h2>Subprocessors</h2>
      <p>To deliver DocLens we share data with the following subprocessors:</p>
      <ul>
        <li><strong>Anthropic</strong> — inference (document → JSON). Anthropic does not train on API traffic by default.</li>
        <li><strong>Supabase</strong> — database, auth, and Edge Function runtime.</li>
        <li><strong>Vercel</strong> — web hosting.</li>
        <li><strong>Sentry</strong> — error monitoring. We scrub file contents before sending.</li>
        <li><strong>Stripe</strong> — billing (paid tiers only).</li>
      </ul>

      <h2>Your rights</h2>
      <p>
        You can delete your account at any time. Account deletion cascades to remove all extractions,
        API keys, and any associated records within 7 days.
      </p>

      <h2>Contact</h2>
      <p>
        Questions? Email{" "}
        <a href="mailto:privacy@doclens.app" className="underline">
          privacy@doclens.app
        </a>
        .
      </p>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updated="2026-05-13">
      <p>
        These Terms govern your use of DocLens. By creating an account, you agree to these Terms.
      </p>

      <h2>The service</h2>
      <p>
        DocLens extracts structured data from documents you upload. We provide the service "as-is"
        and make commercially reasonable efforts to maintain accuracy and availability, but
        AI-generated output may contain errors and you should not rely on it for safety-critical
        decisions without human review.
      </p>

      <h2>Account responsibility</h2>
      <p>
        You are responsible for the documents you upload, including obtaining any necessary rights
        and consents. Do not upload content that you don't have the right to process.
      </p>

      <h2>Prohibited use</h2>
      <ul>
        <li>Extracting personal data of individuals without their consent.</li>
        <li>Processing illegal content.</li>
        <li>Attempting to circumvent quota limits or rate limits.</li>
        <li>Reverse-engineering the service or its prompts.</li>
      </ul>

      <h2>Billing</h2>
      <p>
        Paid plans are billed in advance on a monthly basis. You can cancel at any time; access
        continues through the end of the billing period. Overage charges accrue monthly and bill
        with your next regular cycle.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        Trijazel's liability for any claim arising out of these Terms is limited to the amount you
        paid in the 12 months preceding the claim.
      </p>

      <h2>Contact</h2>
      <p>
        Questions? Email{" "}
        <a href="mailto:legal@doclens.app" className="underline">
          legal@doclens.app
        </a>
        .
      </p>
    </LegalLayout>
  );
}

function LegalLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 md:py-16 space-y-4 prose-doclens">
      <header className="space-y-1 mb-8">
        <h1 className="text-4xl font-bold">{title}</h1>
        <p className="text-sm text-neutral-500">Last updated: {updated}</p>
      </header>
      <div className="space-y-4 text-neutral-700 leading-relaxed [&_h2]:font-semibold [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-neutral-900 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:underline">
        {children}
      </div>
      <p className="text-xs text-neutral-500 pt-8">
        Need a different format? <Link to="/" className="underline">Back to DocLens</Link>.
      </p>
    </div>
  );
}
